import React from "react";

/**
 * ProfileHalftoneHalo – animert halftone-prikk-ramme rundt profilbilde
 * - Bruker alfakanal som maske for halftone-effekt
 * - Rød støy-tekstur som beveger seg nedover bak masken
 * - GPU-vennlig animasjon og respekterer prefers-reduced-motion
 */
export type ProfileHalftoneHaloProps = {
  avatarSrc: string;
  size?: number;
  maskSrc?: string;
  noise1x?: string;
  noise2x?: string;
  avatarPercent?: number;
  pulse?: boolean;
  className?: string;
  onImageLoad?: () => void;
  onImageError?: () => void;
};

export default function ProfileHalftoneHalo({
  avatarSrc,
  size = 280,
  maskSrc = "/img/halftone_red_transparent.png",
  noise1x = "/tex/red-noise-64.png",
  noise2x = "/tex/red-noise-96.png",
  avatarPercent = 60,
  pulse = false,
  className = "",
  onImageLoad,
  onImageError,
}: ProfileHalftoneHaloProps) {
  const avatarSize = `${avatarPercent}%`;

  return (
    <div
      className={`relative inline-block select-none ${className}`}
      style={{ width: size, height: size }}
    >
      {/* Halftone halo wrapper med maske */}
      <div
        className={pulse ? "absolute inset-0 halftone-pulse" : "absolute inset-0"}
        style={{
          WebkitMask: `url(${maskSrc}) center / contain no-repeat`,
          mask: `url(${maskSrc}) center / contain no-repeat`,
          borderRadius: "9999px",
        }}
        aria-hidden
      >
        {/* Animert støy-lag */}
        <div
          className="absolute inset-0 halftone-drift"
          style={{
            backgroundImage: `image-set(url(${noise1x}) 1x, url(${noise2x}) 2x)`,
            backgroundRepeat: "repeat",
            backgroundSize: "256px 256px",
            opacity: 0.9,
          }}
        />
      </div>

      {/* Profilbilde */}
      <img
        src={avatarSrc}
        alt="Profile"
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full object-cover shadow-sm"
        style={{ width: avatarSize, height: avatarSize }}
        loading="lazy"
        onLoad={onImageLoad}
        onError={onImageError}
      />

      {/* Keyframes */}
      <style>{`
        @keyframes halftoneDrift { 
          0% { background-position: 0 0; } 
          100% { background-position: 0 256px; } 
        }
        @keyframes halftonePulse { 
          0%, 100% { opacity: 0.9; transform: scale(1); } 
          50% { opacity: 1; transform: scale(1.05); } 
        }
        .halftone-drift { animation: halftoneDrift 12s linear infinite; }
        .halftone-pulse { animation: halftonePulse 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .halftone-drift, .halftone-pulse { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

/*
Usage:
  <ProfileHalftoneHalo
    avatarSrc="/img/avatar.jpg"
    size={320}
    maskSrc="/img/halftone_red_transparent.png"
    noise1x="/tex/red-noise-64.png"
    noise2x="/tex/red-noise-96.png"
    avatarPercent={60}
    pulse
  />
*/